/**
 * Room-level RBAC (per agentic-network pool) — distinct from workspace
 * RBAC (workspace OWNER/ADMIN/CREATOR/USER).
 *
 * Roles a user can hold within a single pool/room (multiple allowed):
 *   - OWNER       — full control, can respond to builder approvals
 *   - BUILDER     — can respond to builder approvals (cannot manage roster)
 *   - SUPERVISOR  — can view stream and audit; cannot respond
 *   - VIEWER      — read-only spectator
 *
 * Workspace OWNER/ADMIN are also implicitly authorized via "Overwatch"
 * escalation — they can manage any room in the tenant.
 *
 * Legacy mapping: pool ``CREATOR`` rows are surfaced as ``OWNER`` by the
 * backend, but this module also handles them defensively.
 */

export const ROOM_ROLES = Object.freeze({
  OWNER: 'OWNER',
  BUILDER: 'BUILDER',
  SUPERVISOR: 'SUPERVISOR',
  VIEWER: 'VIEWER',
});

const CANONICAL = new Set(Object.values(ROOM_ROLES));
const LEGACY = { CREATOR: ROOM_ROLES.OWNER };

export function normalizeRoomRole(role) {
  const r = String(role ?? '').trim().toUpperCase();
  if (!r) return '';
  if (LEGACY[r]) return LEGACY[r];
  if (CANONICAL.has(r)) return r;
  return r; // unknown: surface as-is for forward compat (UI can flag)
}

export function normalizeRoomRoles(roles) {
  if (!Array.isArray(roles)) return [];
  const seen = new Set();
  const out = [];
  for (const r of roles) {
    const n = normalizeRoomRole(r);
    if (CANONICAL.has(n) && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/**
 * Capability: respond to a Universal Builder approval prompt.
 *
 * Authorized:
 *   - Room OWNER or BUILDER
 *   - Workspace OWNER or ADMIN (Overwatch — operational escalation)
 *
 * Extend by adding role checks here without touching call sites.
 *
 * @param {string[]} roomRoles — current user's roles in this pool/room
 * @param {string} workspaceRole — current user's normalized workspace role
 * @returns {boolean}
 */
export function canRespondToBuilderApproval(roomRoles, workspaceRole) {
  const roles = new Set(normalizeRoomRoles(roomRoles));
  if (roles.has(ROOM_ROLES.OWNER) || roles.has(ROOM_ROLES.BUILDER)) return true;
  const ws = String(workspaceRole ?? '').trim().toUpperCase();
  if (ws === 'OWNER' || ws === 'ADMIN') return true;
  return false;
}

/**
 * Capability: manage the room's member roster (add/remove members,
 * change roles).
 */
export function canManageRoomRoster(roomRoles, workspaceRole) {
  const roles = new Set(normalizeRoomRoles(roomRoles));
  if (roles.has(ROOM_ROLES.OWNER)) return true;
  const ws = String(workspaceRole ?? '').trim().toUpperCase();
  if (ws === 'OWNER' || ws === 'ADMIN') return true;
  return false;
}

/**
 * Capability: pin / clear runtime connector credentials at the pool
 * level — both per-agent overrides and pool-wide defaults.
 *
 * Wider than the roster gate by design: BUILDER is the role that
 * already responds to Universal Builder approvals, so they are the ones
 * who notice "this run is using the wrong git account" mid-flight and
 * need to swap credentials. OWNER also qualifies (full control), and
 * workspace OWNER/ADMIN bypass via Overwatch.
 *
 * @param {string[]} roomRoles
 * @param {string} workspaceRole
 * @returns {boolean}
 */
export function canManageRoomConnectors(roomRoles, workspaceRole) {
  const roles = new Set(normalizeRoomRoles(roomRoles));
  if (roles.has(ROOM_ROLES.OWNER) || roles.has(ROOM_ROLES.BUILDER)) return true;
  const ws = String(workspaceRole ?? '').trim().toUpperCase();
  if (ws === 'OWNER' || ws === 'ADMIN') return true;
  return false;
}

/**
 * Friendly label for UI display.
 */
export function roomRoleLabel(role) {
  const r = normalizeRoomRole(role);
  switch (r) {
    case ROOM_ROLES.OWNER: return 'Owner';
    case ROOM_ROLES.BUILDER: return 'Builder';
    case ROOM_ROLES.SUPERVISOR: return 'Supervisor';
    case ROOM_ROLES.VIEWER: return 'Viewer';
    default: return r || 'Unknown';
  }
}

/**
 * All roles available for assignment (UI selectors).
 */
export const ASSIGNABLE_ROOM_ROLES = [
  { value: ROOM_ROLES.OWNER, label: 'Owner — full control, can respond to approvals' },
  { value: ROOM_ROLES.BUILDER, label: 'Builder — can respond to builder approvals' },
  { value: ROOM_ROLES.SUPERVISOR, label: 'Supervisor — view & audit only' },
  { value: ROOM_ROLES.VIEWER, label: 'Viewer — read-only spectator' },
];
